import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
  createTheme,
} from "@mui/material";
import React, { useContext, useState } from 'react';
import { ToastContainer } from "react-toastify";
import AppBarMenu from '../components/AppBarMenu';
import CustomRadioGroup from "../components/management/CustomRadioGroup";
import WhatsAppManager from '../components/whatsapp/WhatsAppManager';
import { ApiDataContext } from "../contexts/ApiDataContext";
import useDialog from "../hooks/dialog/useDialog";
import { useManagementTextField } from "../hooks/forms/ManagementHooks";
import { useRadioGroup } from "../hooks/forms/RadioGroupHooks";
import { useLoadingToast } from "../hooks/toast/toasts";
import { sendClientsMessage } from "../services/api/api";

const theme = createTheme({
  palette: {
    primary: {
      main: "#128C7E",
    },
  },
});

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

/**
 * Página principal para gerenciamento WhatsApp
 * Seguindo padrão arquitetural do projeto JBE Engenharia
 */
const WhatsAppPage = () => {
  const { apiData } = useContext(ApiDataContext);
  const { initializeLoadingToast, updateLoadingToast } = useLoadingToast();

  //START -> Message Dialog items
  const {
    open: messageDialogOpen,
    handleClickOpen: openMessageDialog,
    handleClickClose: closeMessageDialog,
  } = useDialog();

  const [messageRequestLoading, setMessageRequestLoading] = useState(false);

  const sendMessage = async () => {
    let messageToastId;
    try {
      setMessageRequestLoading(true);
      messageToastId = initializeLoadingToast({
        text: `Enviando Mensagem a Integradores...`,
      });

      const { status } = await sendClientsMessage(
        selectedMessageClients,
        message.value,
        statusFilter.value
      );

      if (status === 200) {
        updateLoadingToast({
          id: messageToastId,
          text: `Mensagem enviada com sucesso`,
          type: "success",
        });
      }
    } catch (error) {
      updateLoadingToast({
        id: messageToastId,
        text: error.response.data.message ?? `Erro ao Enviar Mensagem, tente novamente`,
        type: "error",
      });
    } finally {
      setMessageRequestLoading(false);
      closeMessageDialog();
    }
  };

  const [selectedMessageClients, setSelectedMessageClients] = useState([]);

  const selectMessageClients = (event) => {
    setSelectedMessageClients(event.target.value);
    const value = event.target.value;
    if (value[value.length - 1] === "all") {
      if (selectedMessageClients.length === apiData.clients.length) {
        setSelectedMessageClients([]);
      } else {
        setSelectedMessageClients(apiData.clients);
      }
      return;
    }
    setSelectedMessageClients(value);
  };

  const isMessageClientChecked = (selected) => {
    let isEqual = false;

    selectedMessageClients.forEach((e) => {
      if (e.id === selected.id) {
        isEqual = true;
      }
    });
    return isEqual;
  };

  const areAllMessageSelected =
    apiData.clients.length > 0 && selectedMessageClients.length === apiData.clients.length;

  const { reset: resetMessage, ...message } = useManagementTextField("");
  //END -> Message Dialog items

  const statusFilter = useRadioGroup("onlyActive");

  const clientsSelector = ({ value, handleChange, handleCheck, areAllSelected }) => {
    return (
      <FormControl sx={{ width: "100%", mt: 0.5 }}>
        <InputLabel>Integradores</InputLabel>
        <Select
          multiple
          value={value}
          onChange={handleChange}
          input={<OutlinedInput label="Integradores" />}
          renderValue={(client) => {
            const initialValue = "";
            const str = client.reduce(
              (accumulator, currentValue) => accumulator + currentValue.name + ", ",
              initialValue
            );
            return str;
          }}
          MenuProps={MenuProps}
        >
          <MenuItem value="all">
            <ListItemIcon>
              <Checkbox
                checked={areAllSelected}
                indeterminate={value.length > 0 && value.length < apiData.clients.length}
              />
            </ListItemIcon>
            <ListItemText>
              <Typography sx={{ fontWeight: "bold" }}>Selecione todos</Typography>
            </ListItemText>
          </MenuItem>

          {apiData.clients.map((value) => (
            <MenuItem key={value.id} value={value}>
              <Checkbox checked={handleCheck(value)} />
              <ListItemText primary={value.name} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const createDialog = ({
    title,
    content,
    isOpen,
    isLoading,
    handleClose,
    handleSend,
    height = 150,
  }) => {
    return (
      <Dialog open={isOpen} onClose={handleClose}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent sx={{ width: 600, height }}>{content}</DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading} variant="outlined">
            Cancelar
          </Button>

          <Button
            onClick={handleSend}
            disabled={isLoading}
            variant="contained"
            theme={theme}
            autoFocus
          >
            {isLoading ? "Enviando..." : "Enviar"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const barButtons = (
    <Box
      sx={{
        marginLeft: "auto",
        marginRight: "50px",
        display: "flex",
        justifyContent: "space-around",
        width: "200px",
      }}
    >
      <Button
        variant="contained"
        theme={theme}
        startIcon={<ElectricBoltIcon />}
        onClick={openMessageDialog}
      >
        Mensagem Rápida
      </Button>
    </Box>
  );

  return (
    <AppBarMenu icons={barButtons}>
      {createDialog({
        title: "Mensagem rápida para os integradores",
        height: 325,
        content: (
          <Box>
            {clientsSelector({
              value: selectedMessageClients,
              handleChange: selectMessageClients,
              handleCheck: isMessageClientChecked,
              areAllSelected: areAllMessageSelected,
            })}
            <TextField
              sx={{ mt: 2, mb: 2 }}
              label="Mensagem"
              multiline
              fullWidth
              rows={6}
              disabled={messageRequestLoading}
              value={message.value}
              onChange={message.onChange}
            />
            <CustomRadioGroup
              radioGroupParams={statusFilter}
              label={"Filtro por status:"}
              radioValues={["onlyActive", "onlyInactive", "all"]}
              radioLabels={["Apenas Ativos", "Apenas Inativos", "Todos"]}
            />
          </Box>
        ),
        isOpen: messageDialogOpen,
        isLoading: messageRequestLoading,
        handleClose: closeMessageDialog,
        handleSend: sendMessage,
      })}

      <ToastContainer />
      
      <WhatsAppManager />
    </AppBarMenu>
  );
};

export default WhatsAppPage;
